import { GQLResponseError } from '@change-corgi/core/gqlErrors';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type {
	AddRecommendedDecisionMakerMutation,
	AddRecommendedDecisionMakerMutationVariables,
} from './addRecommendedDecisionMaker.graphql';
import { AddRecommendedDecisionMaker } from './addRecommendedDecisionMaker.graphql';
import { ERROR_MAP } from './constants';

type RecommendedDecisionMaker = {
	normalizedId: string;
	canonicalPetitionTargetId: string | null;
	name: string;
	type: 'PERSON' | 'ORG';
	title: string | null;
	photoUrl: string | null;
};

type AddRecommendedDecisionMakerParams = {
	petitionId: string;
	recommendedDecisionMaker: RecommendedDecisionMaker;
} & { trackingContext: string };

type AddRecommendedDecisionMakerResponse = Extract<
	AddRecommendedDecisionMakerMutation['addDecisionMakerToPetition'],
	{ __typename: 'AddDecisionMakerToPetitionSuccess' }
>;

export async function editPetitionDecisionMakers(
	{ petitionId, recommendedDecisionMaker, trackingContext }: AddRecommendedDecisionMakerParams,
	{ gql: { fetch }, errorReporter }: UtilityContext,
): Promise<AddRecommendedDecisionMakerResponse> {
	const petitionInput = {
		id: petitionId,
		decisionMaker: {
			id: recommendedDecisionMaker.canonicalPetitionTargetId,
			displayName: recommendedDecisionMaker.name,
		},
	};
	let data: AddRecommendedDecisionMakerMutation | undefined;
	let requestId: string | undefined;
	try {
		({ data, requestId } = await fetch<
			AddRecommendedDecisionMakerMutation,
			AddRecommendedDecisionMakerMutationVariables
		>({
			query: AddRecommendedDecisionMaker,
			variables: {
				input: petitionInput,
			},
			rejectOnError: true,
		}));
	} catch (error) {
		if (error instanceof GQLResponseError) {
			throw new Error(ERROR_MAP.GRAPHQL_ERROR, { cause: error });
		}
		throw error;
	}
	if (data.addDecisionMakerToPetition.__typename === 'AddDecisionMakerToPetitionError') {
		void errorReporter.report({
			error: new Error('DM Recommender modal error'),
			context: { requestId },
			params: {
				context: trackingContext,
				errorType: data.addDecisionMakerToPetition.type,
			},
		});
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
		throw new Error(ERROR_MAP[data.addDecisionMakerToPetition.type as unknown as keyof typeof ERROR_MAP]);
	}

	return data.addDecisionMakerToPetition;
}
